/**
 * Page de détail d'une inscription - Parent.
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { inscriptionService, documentService, paiementService, aideService, getMediaUrl } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import jsPDF from 'jspdf';
import { notify } from '../components/NotificationContainer';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import logger from '../utils/logger';

const InscriptionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [inscription, setInscription] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [paiements, setPaiements] = useState([]);
  const [aides, setAides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('infos');
  const [formData, setFormData] = useState({});

  // État pour la création d'échéancier personnalisé
  const [nombreEcheances, setNombreEcheances] = useState(1);
  const [echeancesCustom, setEcheancesCustom] = useState([]);
  const [showCustomForm, setShowCustomForm] = useState(false);

  // État pour l'édition d'échéances existantes
  const [editingPaiements, setEditingPaiements] = useState({});

  // État pour le formulaire d'ajout de document autre
  const [showDocumentAutreForm, setShowDocumentAutreForm] = useState(false);
  const [documentAutreData, setDocumentAutreData] = useState({
    titre: '',
    commentaire: '',
    fichier: null
  });

  // État pour la modale de bienvenue
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  // État pour afficher/masquer les informations de paiement
  const [showPaymentInfo, setShowPaymentInfo] = useState(false);

  // État pour la modale de confirmation de paiement
  const [showPaymentConfirmModal, setShowPaymentConfirmModal] = useState(false);

  useEffect(() => {
    loadInscription();

    // Afficher la modale de bienvenue seulement si jamais vue
    const hasSeenWelcome = localStorage.getItem(`welcome_modal_seen_${id}`);
    if (!hasSeenWelcome) {
      setShowWelcomeModal(true);
    }
  }, [id]);

  // Gérer le paramètre ?tab= dans l'URL pour ouvrir directement le bon onglet
  useEffect(() => {
    if (tabParam && ['infos', 'documents', 'paiements'].includes(tabParam)) {
      setActiveTab(tabParam);
    } else if (!tabParam) {
      // Si pas de paramètre tab, revenir à l'onglet infos par défaut
      setActiveTab('infos');
    }
  }, [tabParam]);

  const loadInscription = async () => {
    try {
      const [inscResp, docsResp, paymResp, aidesResp] = await Promise.all([
        inscriptionService.getOne(id),
        documentService.parInscription(id),
        paiementService.parInscription(id),
        aideService.parInscription(id)
      ]);

      setInscription(inscResp.data);
      setFormData(inscResp.data);
      setDocuments(docsResp.data);
      setPaiements(paymResp.data);
      setAides(aidesResp.data);
    } catch (error) {
      logger.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculer l'âge de l'enfant à la date de début du séjour
  const calculateAgeAtSejour = () => {
    if (!inscription?.enfant_details?.date_naissance || !inscription?.sejour_details?.date_debut) {
      return null;
    }

    const birthDate = new Date(inscription.enfant_details.date_naissance);
    const sejourDate = new Date(inscription.sejour_details.date_debut);

    let age = sejourDate.getFullYear() - birthDate.getFullYear();
    const monthDiff = sejourDate.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && sejourDate.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };

  const handleUploadDocument = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      notify.error('Le fichier est trop volumineux (10 MB maximum)');
      return;
    }

    const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png'];
    const extension = file.name.split('.').pop().toLowerCase();
    if (!allowedExtensions.includes(extension)) {
      notify.error('Format non accepté. Formats autorisés : PDF, JPG, PNG');
      return;
    }

    const formData = new FormData();
    formData.append('inscription', id);
    formData.append('type_document', type);
    formData.append('titre', file.name);
    formData.append('fichier', file);

    try {
      await documentService.upload(formData);
      notify.success('Document téléchargé avec succès ! Notre équipe va le vérifier dans les 48h.');
      loadInscription();
    } catch (error) {
      logger.error('Erreur upload:', error);

      // Essayer de récupérer un message d'erreur spécifique pour le champ 'fichier'
      let errorMsg = 'Erreur lors du téléchargement';

      if (error.response?.data) {
        const data = error.response.data;
        if (data.fichier && Array.isArray(data.fichier)) {
          errorMsg = data.fichier[0];
        } else if (data.message) {
          errorMsg = data.message;
        } else if (data.error) {
          errorMsg = data.error;
        } else if (data.detail) {
          errorMsg = data.detail;
        }
      }

      notify.error(errorMsg);
    }
  };

  const handleReplaceDocument = async (docId, file) => {
    if (!file) return;

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      notify.error('Le fichier est trop volumineux (10 MB maximum)');
      return;
    }

    const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png'];
    const extension = file.name.split('.').pop().toLowerCase();
    if (!allowedExtensions.includes(extension)) {
      notify.error('Format non accepté. Formats autorisés : PDF, JPG, PNG');
      return;
    }

    const formData = new FormData();
    formData.append('fichier', file);

    try {
      await documentService.remplacer(docId, formData);
      notify.success('Document remplacé avec succès ! Notre équipe va le vérifier à nouveau.');
      loadInscription();
    } catch (error) {
      logger.error('Erreur remplacement:', error);
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Erreur lors du remplacement';
      notify.error(errorMsg);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Supprimer ce document ?')) return;

    try {
      await documentService.delete(docId);
      loadInscription();
    } catch (error) {
      notify.error('Erreur lors de la suppression');
    }
  };

  const handleDeclareNonObtenu = async (type) => {
    const confirmation = window.confirm(
      'Votre enfant n\'a pas obtenu le Pass\'nautique ?\n\n' +
      'En cliquant sur OK, vous déclarez que votre enfant n\'a pas obtenu ce certificat.\n' +
      'Notre équipe validera cette déclaration et votre dossier sera considéré comme complet.'
    );

    if (!confirmation) return;

    try {
      // Créer un PDF simple avec jsPDF
      const doc = new jsPDF();

      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');

      const enfantNom = `${inscription.enfant_details?.prenom || ''} ${inscription.enfant_details?.nom || ''}`.trim();
      const parentNom = `${inscription.responsable_prenom || ''} ${inscription.responsable_nom || ''}`.trim();

      const text = [
        'Le parent a indiqué que l\'enfant n\'a pas obtenu',
        'le Pass\'nautique (attestation d\'aisance aquatique).',
        '',
        enfantNom ? `Enfant : ${enfantNom}` : '',
        parentNom ? `Parent : ${parentNom}` : '',
        '',
        `Date : ${new Date().toLocaleDateString('fr-FR')}`
      ].filter(line => line !== ''); // Retirer les lignes vides si pas de nom

      let yPos = 50;
      text.forEach(line => {
        doc.text(line, 105, yPos, { align: 'center' });
        yPos += 10;
      });

      // Convertir le PDF en blob
      const pdfBlob = doc.output('blob');

      // Uploader le document
      const formData = new FormData();
      formData.append('inscription', id);
      formData.append('type_document', type);
      formData.append('titre', 'Pass\'nautique non obtenu');
      formData.append('description', 'Le parent a indiqué que l\'enfant n\'a pas obtenu le Pass\'nautique.');
      formData.append('fichier', pdfBlob, 'pass_nautique_non_obtenu.pdf');

      await documentService.upload(formData);
      notify.success('Déclaration envoyée avec succès !\n\nNotre équipe va la vérifier. Une fois validée, votre dossier sera complet.');
      loadInscription();
    } catch (error) {
      logger.error('Erreur:', error);
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Erreur lors de la déclaration';
      notify.error(errorMsg);
    }
  };

  const handleOpenDocumentAutreForm = () => {
    setDocumentAutreData({
      titre: '',
      commentaire: '',
      fichier: null
    });
    setShowDocumentAutreForm(true);
  };

  const handleCloseDocumentAutreForm = () => {
    setShowDocumentAutreForm(false);
    setDocumentAutreData({
      titre: '',
      commentaire: '',
      fichier: null
    });
  };

  const handleSubmitDocumentAutre = async (e) => {
    e.preventDefault();

    // Validation
    if (!documentAutreData.titre || documentAutreData.titre.trim() === '') {
      notify.error('Le type de document est obligatoire');
      return;
    }

    if (!documentAutreData.fichier) {
      notify.error('Veuillez sélectionner un fichier');
      return;
    }

    // Vérifier la taille
    const maxSize = 10 * 1024 * 1024;
    if (documentAutreData.fichier.size > maxSize) {
      notify.error('Le fichier est trop volumineux (10 MB maximum)');
      return;
    }

    // Vérifier l'extension
    const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png'];
    const extension = documentAutreData.fichier.name.split('.').pop().toLowerCase();
    if (!allowedExtensions.includes(extension)) {
      notify.error('Format non accepté. Formats autorisés : PDF, JPG, PNG');
      return;
    }

    // Calculer le prochain numéro de document autre
    const documentsAutres = documents.filter(d => d.type_document === 'AUTRE');
    const numeroDoc = documentsAutres.length + 1;

    if (numeroDoc > 4) {
      notify.warning('Vous avez atteint la limite de 4 documents autres');
      return;
    }

    const formData = new FormData();
    formData.append('inscription', id);
    formData.append('type_document', 'AUTRE');
    formData.append('numero_document_autre', numeroDoc);
    formData.append('titre', documentAutreData.titre.trim());
    formData.append('commentaire_parent', documentAutreData.commentaire.trim());
    formData.append('fichier', documentAutreData.fichier);

    try {
      await documentService.upload(formData);
      notify.success('Document téléchargé avec succès ! Notre équipe va le vérifier dans les 48h.');
      handleCloseDocumentAutreForm();
      loadInscription();
    } catch (error) {
      logger.error('Erreur upload:', error);
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Erreur lors du téléchargement';
      notify.error(errorMsg);
    }
  };

  const handleSupprimerEcheancier = async () => {
    if (!window.confirm('Voulez-vous vraiment supprimer l\'échéancier actuel ? Vous pourrez en créer un nouveau ensuite.')) return;

    try {
      await paiementService.supprimerEcheancier(id);
      notify.success('Échéancier supprimé avec succès');
      loadInscription();
    } catch (error) {
      notify.error('Erreur lors de la suppression de l\'échéancier');
    }
  };

  const handleSupprimerPaiementsNonValides = async () => {
    const paiementsNonValides = paiements.filter(p => p.statut !== 'VALIDE');

    if (paiementsNonValides.length === 0) {
      notify.info('Tous les paiements sont déjà validés.');
      return;
    }

    if (!window.confirm(`Voulez-vous vraiment supprimer les ${paiementsNonValides.length} paiement(s) non validé(s) ? Vous pourrez ensuite créer un nouvel échéancier pour le solde restant.`)) return;

    try {
      // Supprimer chaque paiement non validé
      for (const paiement of paiementsNonValides) {
        await paiementService.delete(paiement.id);
      }
      notify.success('Paiements non validés supprimés avec succès');
      loadInscription();
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors de la suppression des paiements non validés');
    }
  };

  const initCustomEcheances = (nombre) => {
    const soldeRestant = parseFloat(inscription.solde_restant) || 0;
    const montantParEcheance = soldeRestant / nombre;

    const newEcheances = [];
    for (let i = 0; i < nombre; i++) {
      newEcheances.push({
        montant: montantParEcheance.toFixed(2),
        mode_paiement: 'CARTE',
        date_echeance: ''
      });
    }
    setEcheancesCustom(newEcheances);
    setShowCustomForm(true);
  };

  const handleEcheanceChange = (index, field, value) => {
    const newEcheances = [...echeancesCustom];
    newEcheances[index][field] = value;
    setEcheancesCustom(newEcheances);
  };

  const calculateTotalEcheances = () => {
    return echeancesCustom.reduce((sum, ech) => sum + parseFloat(ech.montant || 0), 0);
  };

  const handleCreerEcheancierCustom = async () => {
    const totalEcheances = calculateTotalEcheances();
    const soldeRestant = parseFloat(inscription.solde_restant) || 0;

    if (Math.abs(totalEcheances - soldeRestant) > 0.01) {
      const diff = soldeRestant - totalEcheances;
      if (diff > 0) {
        notify.warning(`Attention : Il manque ${diff.toFixed(2)} € dans votre échéancier (Total: ${totalEcheances.toFixed(2)} € / Attendu: ${soldeRestant.toFixed(2)} €)`);
      } else {
        notify.warning(`Attention : Votre échéancier dépasse de ${Math.abs(diff).toFixed(2)} € (Total: ${totalEcheances.toFixed(2)} € / Attendu: ${soldeRestant.toFixed(2)} €)`);
      }
      return;
    }

    try {
      const echeancierData = echeancesCustom.map((ech, index) => ({
        montant: ech.montant,
        mode_paiement: ech.mode_paiement,
        date_echeance: ech.date_echeance || undefined
      }));

      await paiementService.creerEcheancier({
        inscription_id: id,
        mode_paiement: echeancesCustom[0].mode_paiement,
        echeancier_personnalise: echeancierData
      });

      notify.success('Échéancier créé avec succès');
      setShowCustomForm(false);
      setEcheancesCustom([]);
      loadInscription();
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors de la création de l\'échéancier');
    }
  };

  const handleEditPaiement = (paiementId) => {
    const paiement = paiements.find(p => p.id === paiementId);
    setEditingPaiements({
      ...editingPaiements,
      [paiementId]: {
        montant: paiement.montant,
        mode_paiement: paiement.mode_paiement
      }
    });
  };

  const handleCancelEditPaiement = (paiementId) => {
    const newEditing = { ...editingPaiements };
    delete newEditing[paiementId];
    setEditingPaiements(newEditing);
  };

  const handleSavePaiement = async (paiementId) => {
    try {
      const editData = editingPaiements[paiementId];
      await paiementService.update(paiementId, editData);

      const newEditing = { ...editingPaiements };
      delete newEditing[paiementId];
      setEditingPaiements(newEditing);

      notify.success('Paiement modifié avec succès');
      loadInscription();
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors de la modification du paiement');
    }
  };

  const calculatePaiementsTotal = () => {
    let total = 0;
    paiements.forEach(p => {
      const pId = p.id;
      if (editingPaiements[pId]) {
        total += parseFloat(editingPaiements[pId].montant || 0);
      } else {
        total += parseFloat(p.montant || 0);
      }
    });
    return total;
  };

  const calculatePaiementsValides = () => {
    let total = 0;
    paiements.forEach(p => {
      if (p.statut === 'VALIDE') {
        total += parseFloat(p.montant || 0);
      }
    });
    return total;
  };

  const handleDownloadPdf = async () => {
    try {
      // Utiliser l'API backend pour générer le PDF professionnel
      const response = await inscriptionService.genererPdf(id);

      // Créer un blob à partir de la réponse
      const blob = new Blob([response.data], { type: 'application/pdf' });

      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `inscription_${inscription.enfant_details?.nom || 'sejour'}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Nettoyer
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      logger.error('Erreur lors de la génération du PDF:', error);
      notify.error('Erreur lors de la génération du PDF');
    }
  };

  // Ancienne fonction avec jsPDF (conservée en commentaire au cas où)
  const handleDownloadPdfOLD = async () => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      let yPos = 25;

      // Couleurs personnalisées
      const colors = {
        primary: [255, 107, 53],      // Orange vif
        secondary: [255, 183, 77],    // Jaune orangé
        accent: [102, 187, 106],      // Vert doux
        dark: [51, 51, 51],           // Gris foncé
        light: [245, 245, 245],       // Gris très clair
        white: [255, 255, 255]
      };

      // Fonction helper pour ajouter une ligne de texte avec label et valeur
      const addInfoLine = (label, value, yPosition) => {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(90, 90, 90);
        doc.text(label + ':', margin + 8, yPosition);

        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...colors.dark);
        const labelWidth = doc.getTextWidth(label + ': ');
        const maxWidth = pageWidth - margin * 2 - labelWidth - 8;
        const valueText = doc.splitTextToSize(value, maxWidth);
        doc.text(valueText, margin + 8 + labelWidth, yPosition);

        return yPosition + (valueText.length * 5) + 1;
      };

      // Fonction pour vérifier et ajouter une nouvelle page si nécessaire
      const checkNewPage = (requiredSpace) => {
        if (yPos + requiredSpace > pageHeight - 30) {
          doc.addPage();
          yPos = 25;
          return true;
        }
        return false;
      };

      // Fonction pour dessiner un titre de section
      const drawSectionTitle = (title, icon = '') => {
        checkNewPage(15);

        // Bordure gauche colorée
        doc.setFillColor(...colors.primary);
        doc.rect(margin, yPos, 3, 10, 'F');

        // Fond de la section
        doc.setFillColor(...colors.light);
        doc.rect(margin + 3, yPos, pageWidth - 2 * margin - 3, 10, 'F');

        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...colors.dark);
        doc.text(`${icon} ${title}`, margin + 8, yPos + 7);

        yPos += 14;
      };

      // ==================== EN-TÊTE ====================
      // Dégradé de couleur (simulé avec rectangles)
      for (let i = 0; i < 50; i++) {
        const alpha = i / 50;
        const r = Math.round(colors.primary[0] + (colors.secondary[0] - colors.primary[0]) * alpha);
        const g = Math.round(colors.primary[1] + (colors.secondary[1] - colors.primary[1]) * alpha);
        const b = Math.round(colors.primary[2] + (colors.secondary[2] - colors.primary[2]) * alpha);
        doc.setFillColor(r, g, b);
        doc.rect(0, i * 0.9, pageWidth, 0.9, 'F');
      }

      // Logo / Nom de l'association
      doc.setTextColor(...colors.white);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('CRIONS D\'COULEURS', margin, 12);

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('Association de sejours pour enfants', margin, 17);

      // Titre principal
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.text('Recapitulatif d\'inscription', pageWidth / 2, 30, { align: 'center' });

      // Sous-titre avec le séjour
      doc.setFontSize(13);
      doc.setFont('helvetica', 'normal');
      doc.text(inscription.sejour_details?.titre || 'N/A', pageWidth / 2, 38, { align: 'center' });

      if (inscription.sejour_details?.date_debut && inscription.sejour_details?.date_fin) {
        doc.setFontSize(10);
        doc.text(
          `Du ${new Date(inscription.sejour_details.date_debut).toLocaleDateString('fr-FR')} au ${new Date(inscription.sejour_details.date_fin).toLocaleDateString('fr-FR')}`,
          pageWidth / 2,
          44,
          { align: 'center' }
        );
      }

      yPos = 55;
      doc.setTextColor(...colors.dark);

      // ==================== INFORMATIONS DE L'ENFANT ====================
      drawSectionTitle('ENFANT', '👶');

      yPos = addInfoLine('Nom et prenom', `${inscription.enfant_details?.nom || 'N/A'} ${inscription.enfant_details?.prenom || 'N/A'}`, yPos);
      yPos = addInfoLine('Date de naissance', inscription.enfant_details?.date_naissance ? new Date(inscription.enfant_details.date_naissance).toLocaleDateString('fr-FR') : 'N/A', yPos);
      yPos = addInfoLine('Genre', inscription.enfant_details?.genre === 'M' ? 'Masculin' : inscription.enfant_details?.genre === 'F' ? 'Feminin' : 'Autre', yPos);
      yPos += 8;

      // ==================== RESPONSABLE LÉGAL ====================
      drawSectionTitle('RESPONSABLE LEGAL', '📋');

      yPos = addInfoLine('Nom et prenom', `${inscription.responsable_nom || 'N/A'} ${inscription.responsable_prenom || 'N/A'}`, yPos);
      yPos = addInfoLine('Lien avec l\'enfant', inscription.responsable_lien || 'N/A', yPos);
      yPos = addInfoLine('Adresse', inscription.responsable_adresse ? `${inscription.responsable_adresse}, ${inscription.responsable_code_postal || ''} ${inscription.responsable_ville || ''}` : 'N/A', yPos);
      yPos = addInfoLine('Telephone', inscription.responsable_telephone || 'N/A', yPos);
      yPos = addInfoLine('Email', inscription.responsable_email || 'N/A', yPos);
      yPos = addInfoLine('N° Securite Sociale', inscription.numero_securite_sociale || 'N/A', yPos);
      yPos = addInfoLine('Assurance', inscription.compagnie_assurance ? `${inscription.compagnie_assurance}${inscription.numero_contrat_assurance ? ` (${inscription.numero_contrat_assurance})` : ''}` : 'N/A', yPos);
      yPos += 8;

      // ==================== CONTACT D'URGENCE ====================
      drawSectionTitle('CONTACT D\'URGENCE', '🚨');

      yPos = addInfoLine('Nom et prenom', inscription.urgence_nom_prenom || 'N/A', yPos);
      yPos = addInfoLine('Lien avec l\'enfant', inscription.urgence_lien || 'N/A', yPos);
      yPos = addInfoLine('Telephone', inscription.urgence_telephone || 'N/A', yPos);
      yPos += 8;

      // ==================== MÉDECIN TRAITANT ====================
      if (inscription.medecin_nom || inscription.medecin_prenom || inscription.medecin_telephone) {
        drawSectionTitle('MEDECIN TRAITANT', '⚕️');

        yPos = addInfoLine('Nom et prenom', `Dr ${inscription.medecin_nom || ''} ${inscription.medecin_prenom || ''}`, yPos);
        yPos = addInfoLine('Telephone', inscription.medecin_telephone || 'N/A', yPos);
        yPos += 8;
      }

      // ==================== INFORMATIONS COMPLÉMENTAIRES ====================
      if (inscription.regime_alimentaire || inscription.remarques) {
        drawSectionTitle('INFORMATIONS COMPLEMENTAIRES', '🍽️');

        if (inscription.regime_alimentaire && inscription.regime_alimentaire !== 'AUCUN') {
          const regime = inscription.regime_alimentaire === 'SANS_PORC' ? 'Sans porc' :
            inscription.regime_alimentaire === 'VEGETARIEN' ? 'Vegetarien' :
              inscription.regime_alimentaire === 'VEGAN' ? 'Vegan' :
                inscription.regime_alimentaire_autre || inscription.regime_alimentaire;
          yPos = addInfoLine('Regime alimentaire', regime, yPos);
        }

        if (inscription.remarques) {
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(90, 90, 90);
          doc.text('Remarques:', margin + 8, yPos);
          yPos += 5;

          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...colors.dark);
          const remarquesLines = doc.splitTextToSize(inscription.remarques, pageWidth - 2 * margin - 16);
          remarquesLines.forEach(line => {
            checkNewPage(6);
            doc.text(line, margin + 8, yPos);
            yPos += 5;
          });
        }
        yPos += 8;
      }

      // ==================== RÉCAPITULATIF FINANCIER ====================
      drawSectionTitle('RECAPITULATIF FINANCIER', '💰');

      // Box avec bordure arrondie pour le récapitulatif financier
      const financialBoxY = yPos;
      doc.setDrawColor(...colors.primary);
      doc.setLineWidth(0.5);
      doc.roundedRect(margin, financialBoxY, pageWidth - 2 * margin, 40, 2, 2, 'S');

      yPos += 8;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      doc.text('Prix du sejour:', margin + 8, yPos);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...colors.dark);
      doc.text(`${(parseFloat(inscription.montant_total) || 0).toFixed(2)} €`, pageWidth - margin - 8, yPos, { align: 'right' });

      yPos += 8;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      doc.text('Aides obtenues:', margin + 8, yPos);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...colors.accent);
      doc.text(`- ${(parseFloat(inscription.total_aides) || 0).toFixed(2)} €`, pageWidth - margin - 8, yPos, { align: 'right' });

      yPos += 8;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      doc.text('Prix final:', margin + 8, yPos);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(...colors.primary);
      doc.text(`${(parseFloat(inscription.prix_final) || 0).toFixed(2)} €`, pageWidth - margin - 8, yPos, { align: 'right' });

      yPos += 9;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      doc.text('Reste a payer:', margin + 8, yPos);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      const soldeRestant = parseFloat(inscription.solde_restant) || 0;
      doc.setTextColor(soldeRestant > 0 ? colors.secondary[0] : colors.accent[0],
        soldeRestant > 0 ? colors.secondary[1] : colors.accent[1],
        soldeRestant > 0 ? colors.secondary[2] : colors.accent[2]);
      doc.text(`${soldeRestant.toFixed(2)} €`, pageWidth - margin - 8, yPos, { align: 'right' });

      yPos += 15;
      doc.setTextColor(...colors.dark);
      doc.setFontSize(10);

      // ==================== ÉCHÉANCIER DE PAIEMENT ====================
      if (paiements.length > 0) {
        drawSectionTitle('ECHEANCIER DE PAIEMENT', '📅');

        paiements.forEach((p, index) => {
          checkNewPage(22);

          // Box pour chaque paiement avec bordure colorée
          const boxY = yPos;
          const boxHeight = 20;

          // Bordure gauche colorée selon le statut
          const statutColor = p.statut === 'VALIDE' ? colors.accent :
            p.statut === 'EN_ATTENTE' ? colors.secondary : [239, 83, 80];
          doc.setFillColor(...statutColor);
          doc.rect(margin, boxY, 3, boxHeight, 'F');

          // Fond de la box
          doc.setFillColor(index % 2 === 0 ? 252 : 248, index % 2 === 0 ? 252 : 248, index % 2 === 0 ? 252 : 248);
          doc.rect(margin + 3, boxY, pageWidth - 2 * margin - 3, boxHeight, 'F');

          // Bordure
          doc.setDrawColor(220, 220, 220);
          doc.setLineWidth(0.3);
          doc.rect(margin, boxY, pageWidth - 2 * margin, boxHeight, 'S');

          yPos += 6;
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...colors.dark);
          doc.text(`Echeance ${p.numero_echeance}`, margin + 8, yPos);

          const mode = p.mode_paiement === 'CARTE' ? 'Carte bancaire' :
            p.mode_paiement === 'CHEQUE' ? 'Cheque' :
              p.mode_paiement === 'VIREMENT' ? 'Virement' :
                p.mode_paiement === 'CHEQUE_VACANCES' ? 'Cheques vacances' : 'Especes';
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
          doc.setTextColor(100, 100, 100);
          doc.text(`${mode}`, margin + 35, yPos);

          doc.setFont('helvetica', 'bold');
          doc.setFontSize(11);
          doc.setTextColor(...colors.primary);
          doc.text(`${p.montant} €`, pageWidth - margin - 45, yPos, { align: 'right' });

          const statut = p.statut === 'VALIDE' ? 'Paye' :
            p.statut === 'EN_ATTENTE' ? 'En attente' : 'Refuse';
          doc.setFontSize(9);
          doc.setTextColor(...statutColor);
          doc.text(statut, pageWidth - margin - 8, yPos, { align: 'right' });

          yPos += 7;
          doc.setTextColor(120, 120, 120);
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          if (p.date_echeance) {
            doc.text(`Date: ${new Date(p.date_echeance).toLocaleDateString('fr-FR')}`, margin + 8, yPos);
          }
          if (p.reference) {
            doc.text(`Ref: ${p.reference}`, margin + 70, yPos);
          }

          yPos += 9;
          doc.setTextColor(...colors.dark);
        });

        yPos += 5;
      }

      // ==================== AIDES FINANCIÈRES ====================
      if (aides.length > 0) {
        drawSectionTitle('AIDES FINANCIERES', '🤝');

        aides.forEach((aide, index) => {
          checkNewPage(20);

          const boxY = yPos;
          const boxHeight = 18;

          // Bordure gauche colorée selon le statut
          const statutColor = aide.statut_accord === 'ACCORDE' ? colors.accent :
            aide.statut_accord === 'EN_ATTENTE' ? colors.secondary : [239, 83, 80];
          doc.setFillColor(...statutColor);
          doc.rect(margin, boxY, 3, boxHeight, 'F');

          // Fond de la box
          const bgColor = index % 2 === 0 ? [240, 255, 240] : [235, 255, 235];
          doc.setFillColor(...bgColor);
          doc.rect(margin + 3, boxY, pageWidth - 2 * margin - 3, boxHeight, 'F');

          // Bordure
          doc.setDrawColor(220, 220, 220);
          doc.setLineWidth(0.3);
          doc.rect(margin, boxY, pageWidth - 2 * margin, boxHeight, 'S');

          yPos += 6;
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...colors.dark);
          doc.text(aide.organisme, margin + 8, yPos);

          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(100, 100, 100);
          doc.text(`${aide.type_aide}`, margin + 8, yPos + 5);

          doc.setFont('helvetica', 'bold');
          doc.setFontSize(11);
          doc.setTextColor(...colors.accent);
          doc.text(`${aide.montant} €`, pageWidth - margin - 45, yPos, { align: 'right' });

          const statut = aide.statut_accord === 'ACCORDE' ? 'Valide' :
            aide.statut_accord === 'EN_ATTENTE' ? 'En attente' : 'Refuse';
          doc.setFontSize(9);
          doc.setTextColor(...statutColor);
          doc.text(statut, pageWidth - margin - 8, yPos, { align: 'right' });

          yPos += 14;
          doc.setTextColor(...colors.dark);
        });

        yPos += 5;
      }

      // ==================== MENTION LÉGALE ====================
      checkNewPage(30);
      yPos += 5;

      // Box pour la mention légale
      doc.setFillColor(255, 250, 240);
      doc.setDrawColor(...colors.secondary);
      doc.setLineWidth(0.5);
      doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 25, 2, 2, 'FD');

      yPos += 5;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...colors.primary);
      doc.text('MENTION IMPORTANTE', margin + 8, yPos);

      yPos += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(100, 100, 100);
      const mentionText = 'Ce document est un recapitulatif informatif et n\'a pas de valeur contractuelle. Il est edite par l\'association Crions d\'Couleurs pour vous permettre de suivre l\'etat de votre inscription. Les informations presentees sont susceptibles d\'evoluer jusqu\'a la confirmation definitive de l\'inscription. Pour toute question, veuillez nous contacter.';
      const mentionLines = doc.splitTextToSize(mentionText, pageWidth - 2 * margin - 16);
      mentionLines.forEach(line => {
        doc.text(line, margin + 8, yPos);
        yPos += 4;
      });

      // ==================== PIED DE PAGE ====================
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);

        // Ligne de séparation
        doc.setDrawColor(220, 220, 220);
        doc.setLineWidth(0.3);
        doc.line(margin, pageHeight - 18, pageWidth - margin, pageHeight - 18);

        // Association
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...colors.primary);
        doc.text('Crions d\'Couleurs', margin, pageHeight - 12);

        // Date et heure de génération
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(130, 130, 130);
        doc.text(
          `Document genere le ${new Date().toLocaleDateString('fr-FR')} a ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`,
          pageWidth / 2,
          pageHeight - 12,
          { align: 'center' }
        );

        // Numéro de page
        doc.text(`Page ${i} / ${totalPages}`, pageWidth - margin, pageHeight - 12, { align: 'right' });
      }

      // Télécharger le PDF
      doc.save(`inscription_${inscription.enfant_details?.nom || 'sejour'}_${new Date().toISOString().split('T')[0]}.pdf`);

    } catch (error) {
      logger.error('Erreur lors de la génération du PDF:', error);
      notify.error('Erreur lors de la génération du PDF');
    }
  };

  if (loading) return <div className="text-center py-12">Chargement...</div>;
  if (!inscription) return <div className="text-center py-12">Inscription introuvable</div>;

  const canEdit = inscription.statut !== 'CONFIRMEE' || user?.isAdmin;
  const soldeRestant = parseFloat(inscription.solde_restant) || 0;
  const prixFinal = parseFloat(inscription.prix_final) || 0;
  const paiementsTotal = calculatePaiementsTotal();
  const paiementsValides = calculatePaiementsValides();
  // Comparer le total de l'échéancier avec le prix final (pas le solde restant)
  const totalDiff = prixFinal - paiementsTotal;

  // Vérifier si les parents peuvent modifier les documents et paiements
  const canManageDocuments = user?.isAdmin || (inscription.statut !== 'ANNULEE' && inscription.statut !== 'EN_ATTENTE_VALIDATION_PARTENAIRE');
  const canManagePaiements = user?.isAdmin || (inscription.statut !== 'ANNULEE' && inscription.statut !== 'EN_ATTENTE_VALIDATION_PARTENAIRE');

  return (
    <div className="w-full min-h-screen">
      <div className="max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
            Inscription - {inscription.enfant_details?.full_name}
          </h1>
          <div className="flex gap-2 sm:space-x-2">
            <button
              onClick={handleDownloadPdf}
              className="flex-1 sm:flex-none bg-primary-600 text-white px-3 sm:px-4 py-2 rounded-md hover:bg-primary-700 active:bg-primary-800 transition-colors text-xs sm:text-sm"
            >
              Télécharger PDF
            </button>
            <button
              onClick={() => navigate('/inscriptions')}
              className="flex-1 sm:flex-none bg-gray-600 text-white px-3 sm:px-4 py-2 rounded-md hover:bg-gray-700 active:bg-gray-800 transition-colors text-xs sm:text-sm"
            >
              Retour
            </button>
          </div>
        </div>

        {/* Encart de bienvenue */}
        {showWelcomeModal && (
          <div className="mb-4 sm:mb-6 bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4 sm:p-5 shadow-sm relative">
            <button
              onClick={() => {
                setShowWelcomeModal(false);
                localStorage.setItem(`welcome_modal_seen_${id}`, 'true');
              }}
              className="absolute top-3 right-3 text-blue-400 hover:text-blue-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3">
              Pour finaliser votre inscription, utilisez les 3 onglets ci-dessous :
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div className="p-3 bg-white rounded-lg border border-blue-100">
                <h4 className="font-semibold text-blue-900 text-sm mb-1">📋 Informations</h4>
                <p className="text-xs text-gray-600">Complétez vos informations personnelles</p>
              </div>

              <div className="p-3 bg-white rounded-lg border border-blue-100">
                <h4 className="font-semibold text-blue-900 text-sm mb-1">📄 Documents</h4>
                <p className="text-xs text-gray-600">Téléchargez les documents requis</p>
              </div>

              <div className="p-3 bg-white rounded-lg border border-blue-100">
                <h4 className="font-semibold text-blue-900 text-sm mb-1">💰 Paiements</h4>
                <p className="text-xs text-gray-600">Créez votre échéancier de paiement</p>
              </div>
            </div>
          </div>
        )}

        {/* Onglets */}
        <div className="border-b border-gray-200 mb-4 sm:mb-6 overflow-x-auto">
          <nav className="-mb-px flex space-x-4 sm:space-x-8 min-w-full">
            {['infos', 'documents', 'paiements'].map((tab) => (
              <button
                key={tab}
                onClick={() => navigate(`/inscriptions/${id}?tab=${tab}`, { replace: true })}
                className={`${activeTab === tab
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm`}
              >
                {tab === 'infos' && 'Informations'}
                {tab === 'documents' && 'Documents'}
                {tab === 'paiements' && 'Paiements et aides'}
              </button>
            ))}
          </nav>
        </div>

        {/* Onglet Informations */}
        {activeTab === 'infos' && (
          <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-4 sm:p-6 space-y-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
              <h3 className="text-base sm:text-lg font-medium">Informations personnelles</h3>
              {inscription.statut !== 'EN_ATTENTE_VALIDATION_PARTENAIRE' && (
                <button
                  onClick={() => navigate(`/inscriptions/${id}/informations`)}
                  className="w-full sm:w-auto bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-md hover:bg-blue-700 active:bg-blue-800 transition-colors text-xs sm:text-sm"
                >
                  {inscription.is_infos_complete ? 'Modifier les informations' : 'Compléter les informations'}
                </button>
              )}
            </div>

            {!inscription.is_infos_complete && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 sm:p-4 mb-4">
                <div className="flex">
                  <div className="ml-2 sm:ml-3">
                    <p className="text-xs sm:text-sm text-yellow-700">
                      Les informations de cette inscription sont incomplètes. Veuillez les compléter pour finaliser l'inscription.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {inscription.statut === 'EN_ATTENTE_VALIDATION_PARTENAIRE' && (
              <div className="bg-orange-50 border-l-4 border-orange-400 p-3 sm:p-4 mb-4">
                <div className="flex">
                  <div className="ml-2 sm:ml-3">
                    <p className="text-xs sm:text-sm text-orange-700">
                      Cette inscription est en attente de validation par nos partenaires sociaux. Vous pourrez compléter les informations une fois l'inscription validée.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Affichage structuré de toutes les informations */}
            <div className="space-y-3 sm:space-y-4">
              {/* Informations de l'enfant */}
              <div className="border border-gray-300 rounded-lg p-3 sm:p-4 bg-gray-50">
                <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-3">👶 Informations de l'enfant</h4>
                <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
                  {(inscription.enfant_details?.photo_url || inscription.enfant_details?.photo) ? (
                    <img
                      src={inscription.enfant_details.photo_url || getMediaUrl(inscription.enfant_details.photo)}
                      alt={`${inscription.enfant_details?.prenom} ${inscription.enfant_details?.nom}`}
                      className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-4 border-white shadow-lg flex-shrink-0 mx-auto sm:mx-0"
                    />
                  ) : (
                    <div className="w-20 h-20 sm:w-24 sm:h-24 bg-white border-4 border-white rounded-full flex items-center justify-center text-3xl sm:text-4xl shadow-lg flex-shrink-0 mx-auto sm:mx-0">
                      {(() => {
                        const ageAtSejour = calculateAgeAtSejour();
                        return ageAtSejour < 6 ? '👶' : ageAtSejour < 12 ? '🧒' : '👦';
                      })()}
                    </div>
                  )}
                  <div className="flex-1 space-y-2 text-xs sm:text-sm w-full">
                    <div className="flex items-baseline gap-2">
                      <span className="text-gray-600">Nom et prénom :</span>
                      <span className="font-bold text-lg text-gray-900">
                        {inscription.enfant_details?.prenom} {inscription.enfant_details?.nom}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Date de naissance :</span>
                      <span className="font-medium ml-2">
                        {inscription.enfant_details?.date_naissance
                          ? new Date(inscription.enfant_details.date_naissance).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
                          : <span className="text-gray-400 italic">Non renseignée</span>
                        }
                      </span>
                      {calculateAgeAtSejour() !== null && (
                        <span className="ml-2 text-blue-700 font-semibold">
                          ({calculateAgeAtSejour()} ans lors du séjour)
                        </span>
                      )}
                    </div>
                    {inscription.enfant_details?.lieu_naissance && (
                      <div>
                        <span className="text-gray-600">Lieu de naissance :</span>
                        <span className="font-medium ml-2">{inscription.enfant_details.lieu_naissance}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-600">Séjour :</span>
                      <span className="font-medium ml-2 text-primary-700">
                        {inscription.sejour_details?.titre || 'N/A'}
                      </span>
                    </div>
                    {inscription.sejour_details?.date_debut && inscription.sejour_details?.date_fin && (
                      <div>
                        <span className="text-gray-600">Période :</span>
                        <span className="font-medium ml-2">
                          Du {new Date(inscription.sejour_details.date_debut).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}
                          {' au '}
                          {new Date(inscription.sejour_details.date_fin).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </span>
                      </div>
                    )}
                    {(inscription.tranche_prix_details || (inscription.inscription_age_min !== null && inscription.inscription_age_max !== null)) && (
                      <div>
                        <span className="text-gray-600">Tranche d'âge inscrite :</span>
                        <span className="font-medium ml-2">
                          {inscription.tranche_prix_details ? (
                            `${inscription.tranche_prix_details.age_min}-${inscription.tranche_prix_details.age_max} ans`
                          ) : (
                            <span className="text-orange-600 inline-flex items-center gap-1">
                              {inscription.inscription_age_min}-{inscription.inscription_age_max} ans
                              <span className="text-xs italic">(archivé)</span>
                            </span>
                          )}
                        </span>
                        {calculateAgeAtSejour() !== null && (
                          <span className="text-xs text-gray-500 ml-1">
                            (âge au séjour: {calculateAgeAtSejour()} ans)
                          </span>
                        )}
                      </div>
                    )}
                    {inscription.sejour && inscription.sejour_details?.page_presentation && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <a
                          href={`${process.env.REACT_APP_API_URL.replace('/api', '')}/api/sejours/${inscription.sejour}/presentation/`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white text-sm font-medium rounded-md transition-colors"
                        >
                          <InformationCircleIcon className="h-5 w-5" />
                          Informations sur le séjour
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Responsable légal */}
              <div className="border border-gray-300 rounded-lg p-3 sm:p-4 bg-gray-50">
                <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-3">📋 Responsable légal</h4>
                <div className="space-y-2 text-xs sm:text-sm">
                  <div><span className="text-gray-600">Nom et prénom :</span> <span className="font-medium">{inscription.responsable_nom && inscription.responsable_prenom ? `${inscription.responsable_nom} ${inscription.responsable_prenom}` : <span className="text-gray-400 italic">Non renseigné</span>}</span></div>
                  <div><span className="text-gray-600">Lien avec l'enfant :</span> <span className="font-medium">{inscription.responsable_lien || <span className="text-gray-400 italic">Non renseigné</span>}</span></div>
                  <div><span className="text-gray-600">Adresse :</span> <span className="font-medium">{inscription.responsable_adresse ? `${inscription.responsable_adresse}, ${inscription.responsable_code_postal || ''} ${inscription.responsable_ville || ''}` : <span className="text-gray-400 italic">Non renseignée</span>}</span></div>
                  <div><span className="text-gray-600">Téléphone :</span> <span className="font-medium">{inscription.responsable_telephone || <span className="text-gray-400 italic">Non renseigné</span>}</span></div>
                  <div><span className="text-gray-600">Email :</span> <span className="font-medium">{inscription.responsable_email || <span className="text-gray-400 italic">Non renseigné</span>}</span></div>
                  <div><span className="text-gray-600">N° Sécurité Sociale :</span> <span className="font-medium">{inscription.numero_securite_sociale || <span className="text-gray-400 italic">Non renseigné</span>}</span></div>
                  <div><span className="text-gray-600">Assurance :</span> <span className="font-medium">{inscription.compagnie_assurance ? `${inscription.compagnie_assurance}${inscription.numero_contrat_assurance ? ` (${inscription.numero_contrat_assurance})` : ''}` : <span className="text-gray-400 italic">Non renseignée</span>}</span></div>
                </div>
              </div>

              {/* Volet social */}
              <div className="border border-gray-300 rounded-lg p-3 sm:p-4 bg-gray-50">
                <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-3">🏛️ Volet social <span className="text-xs text-gray-500 font-normal">(optionnel)</span></h4>
                <div className="space-y-2 text-xs sm:text-sm">
                  <div><span className="text-gray-600">N° MSA/CAF :</span> <span className="font-medium">{inscription.numero_msa_caf || <span className="text-gray-400 italic">Non renseigné</span>}</span></div>
                  <div><span className="text-gray-600">Partenaire affilié :</span> <span className="font-medium">{inscription.partenaire_affilie || <span className="text-gray-400 italic">Non renseigné</span>}</span></div>
                  <div><span className="text-gray-600">Travailleur social référent :</span> <span className="font-medium">{inscription.travailleur_social_referent || <span className="text-gray-400 italic">Non renseigné</span>}</span></div>
                </div>
              </div>

              {/* Contact d'urgence */}
              <div className="border border-gray-300 rounded-lg p-3 sm:p-4 bg-gray-50">
                <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-3">🚨 Contact d'urgence</h4>
                <div className="space-y-2 text-xs sm:text-sm">
                  <div><span className="text-gray-600">Nom et prénom :</span> <span className="font-medium">{inscription.urgence_nom_prenom || <span className="text-gray-400 italic">Non renseigné</span>}</span></div>
                  <div><span className="text-gray-600">Lien avec l'enfant :</span> <span className="font-medium">{inscription.urgence_lien || <span className="text-gray-400 italic">Non renseigné</span>}</span></div>
                  <div><span className="text-gray-600">Téléphone :</span> <span className="font-medium">{inscription.urgence_telephone || <span className="text-gray-400 italic">Non renseigné</span>}</span></div>
                </div>
              </div>

              {/* Médecin traitant */}
              <div className="border border-gray-300 rounded-lg p-3 sm:p-4 bg-gray-50">
                <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-3">⚕️ Médecin traitant <span className="text-xs text-gray-500 font-normal">(optionnel)</span></h4>
                <div className="space-y-2 text-xs sm:text-sm">
                  <div><span className="text-gray-600">Nom et prénom :</span> <span className="font-medium">{inscription.medecin_nom || inscription.medecin_prenom ? `Dr ${inscription.medecin_nom || ''} ${inscription.medecin_prenom || ''}` : <span className="text-gray-400 italic">Non renseigné</span>}</span></div>
                  <div><span className="text-gray-600">Téléphone :</span> <span className="font-medium">{inscription.medecin_telephone || <span className="text-gray-400 italic">Non renseigné</span>}</span></div>
                </div>
              </div>

              {/* Régime alimentaire et remarques */}
              <div className="border border-gray-300 rounded-lg p-3 sm:p-4 bg-gray-50">
                <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-3">🍽️ Informations complémentaires</h4>
                <div className="space-y-2 text-xs sm:text-sm">
                  <div><span className="text-gray-600">Régime alimentaire :</span> <span className="font-medium">{inscription.regime_alimentaire && inscription.regime_alimentaire !== 'AUCUN' ? (inscription.regime_alimentaire === 'SANS_PORC' ? 'Sans porc' : inscription.regime_alimentaire === 'VEGETARIEN' ? 'Végétarien' : inscription.regime_alimentaire === 'VEGAN' ? 'Végan' : inscription.regime_alimentaire_autre) : <span className="text-gray-400 italic">Aucun régime spécifique</span>}</span></div>
                  <div><span className="text-gray-600">Remarques :</span> <div className="mt-1 font-medium whitespace-pre-wrap">{inscription.remarques || <span className="text-gray-400 italic">Aucune remarque</span>}</div></div>
                </div>
              </div>

              {/* Consentement RGPD santé - version discrète */}
              <div className="border border-gray-200 rounded-lg p-2 sm:p-3 bg-gray-50 mt-3">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-xs text-gray-600 mb-1">Consentement RGPD données de santé</p>
                    <div className="flex items-center gap-2">
                      {inscription.health_data_consent && inscription.health_consent_date ? (
                        <>
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                            ✓ Validé
                          </span>
                          <span className="text-xs text-gray-500">
                            le {new Date(inscription.health_consent_date).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })} à {new Date(inscription.health_consent_date).toLocaleTimeString('fr-FR', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700">
                            ⚠ Non donné
                          </span>
                          <span className="text-xs text-gray-500">
                            Requis pour finaliser l'inscription
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <a
                    href="/politique-rgpd-sante"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-gray-500 hover:text-gray-700 ml-3 flex-shrink-0"
                  >
                    En savoir plus →
                  </a>
                </div>
                {inscription.health_data_consent && inscription.health_consent_date && (
                  <p className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-200">
                    Pour modifier votre consentement, contactez-nous à <a href="mailto:cdccolonies@gmail.com" className="text-blue-600 hover:text-blue-800">cdccolonies@gmail.com</a>
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Onglet Documents */}
        {activeTab === 'documents' && (
          <div className="bg-white shadow rounded-lg p-6">
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">📄 Documents obligatoires</h3>
              <p className="text-sm text-gray-600">
                Veuillez télécharger les documents suivants. Ils seront vérifiés par notre équipe avant validation.
              </p>
              <div className="mt-2 text-xs text-gray-500">
                Formats acceptés : PDF, JPG, PNG • Taille max : 10 MB
              </div>
            </div>

            {/* Message d'avertissement si inscription annulée ou en attente validation partenaire */}
            {!canManageDocuments && (
              <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
                <div className="flex">
                  <div className="ml-3">
                    <p className="text-sm text-red-700 font-medium">
                      {inscription.statut === 'ANNULEE'
                        ? '⛔ Cette inscription est annulée. Vous ne pouvez pas ajouter ou modifier les documents.'
                        : '⏳ Cette inscription est en attente de validation par nos partenaires sociaux. Vous pourrez ajouter les documents une fois l\'inscription validée.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {[
                { type: 'FICHE_SANITAIRE', label: 'Fiche sanitaire', icon: '🏥', hasModele: true },
                { type: 'VACCINS', label: 'Carnet de vaccinations', icon: '💉', hasModele: false },
                { type: 'ATTESTATION_AISANCE_AQUATIQUE', label: 'Attestation d\'aisance aquatique (Pass\'nautique)', icon: '🏊', hasModele: false }
              ].map(({ type, label, icon, hasModele }) => {
                const doc = documents.find(d => d.type_document === type);
                return (
                  <div key={type} className="border border-gray-200 rounded-lg p-5 bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-2xl">{icon}</span>
                          <h4 className="font-semibold text-gray-900">{label}</h4>
                        </div>

                        {/* Modèle disponible pour la fiche sanitaire */}
                        {hasModele && !doc && (
                          <div className="mb-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-xs text-blue-700 mb-2">
                              📋 Un modèle est disponible pour vous aider à remplir ce document
                            </p>
                            <button
                              onClick={() => {
                                // CORRECTION MOBILE: Navigation directe vers le PDF au lieu du blob
                                // Cela fonctionne sur tous les navigateurs, y compris mobile
                                const apiUrl = process.env.REACT_APP_API_URL || '/api';
                                window.open(`${apiUrl}/documents/modele_fiche_sanitaire/`, '_blank');
                              }}
                              className="bg-blue-600 text-white px-3 py-1.5 rounded text-xs font-medium hover:bg-blue-700 transition-colors inline-flex items-center gap-1.5"
                            >
                              <span>📄</span>
                              <span>Télécharger le modèle vierge</span>
                            </button>
                          </div>
                        )}

                        {/* Message important pour la fiche sanitaire */}
                        {type === 'FICHE_SANITAIRE' && (
                          <div className="mb-3 bg-orange-50 border border-orange-200 rounded-lg p-3">
                            <p className="text-sm text-orange-800 font-medium mb-1">
                              ⚠️ Important - Original papier requis
                            </p>
                            <p className="text-sm text-orange-700">
                              La fiche sanitaire doit être envoyée en version originale et signée à :
                              <br />
                              <span className="font-semibold">Crions d'Couleurs - 27 rue Notre Dame, 35500 Vitré</span>
                            </p>
                          </div>
                        )}

                        {doc ? (
                          <div className="space-y-3">
                            <div className="flex items-center gap-2">
                              {doc.statut_validation === 'VALIDE' && (
                                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                                  <span className="text-green-600 text-xl">✓</span>
                                  <div>
                                    <p className="text-sm font-medium text-green-800">Document validé</p>
                                    <p className="text-xs text-green-600">Votre document a été approuvé par notre équipe</p>
                                  </div>
                                </div>
                              )}
                              {doc.statut_validation === 'NON_OBTENU' && (
                                <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                                  <span className="text-blue-600 text-xl">⊘</span>
                                  <div>
                                    <p className="text-sm font-medium text-blue-800">Non obtenu - Dossier complet</p>
                                    <p className="text-xs text-blue-600">Votre déclaration a été validée. Votre dossier est complet.</p>
                                    {doc.commentaire_validation && (
                                      <p className="text-xs text-blue-600 mt-1">{doc.commentaire_validation}</p>
                                    )}
                                  </div>
                                </div>
                              )}
                              {doc.statut_validation === 'EN_ATTENTE' && (
                                <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                                  <span className="text-blue-600 text-xl">⏳</span>
                                  <div>
                                    <p className="text-sm font-medium text-blue-800">En cours de vérification</p>
                                    <p className="text-xs text-blue-600">Notre équipe vérifie votre document, vous serez notifié une fois validé</p>
                                  </div>
                                </div>
                              )}
                              {doc.statut_validation === 'REFUSE' && (
                                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                                  <span className="text-red-600 text-xl">✗</span>
                                  <div>
                                    <p className="text-sm font-medium text-red-800">Document refusé</p>
                                    <p className="text-xs text-red-600">Veuillez télécharger un nouveau document</p>
                                    {doc.commentaire_validation && (
                                      <p className="text-xs text-red-600 mt-1">Raison : {doc.commentaire_validation}</p>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>

                            <p className="text-sm text-gray-600">
                              📎 {doc.titre}
                            </p>

                            <div className="flex gap-2 flex-wrap">
                              {doc.statut_validation !== 'VALIDE' && canManageDocuments && (
                                <>
                                  <label className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg cursor-pointer hover:bg-blue-700 transition-colors">
                                    Remplacer le document
                                    <input
                                      type="file"
                                      className="hidden"
                                      accept=".pdf,.jpg,.jpeg,.png"
                                      onChange={(e) => handleReplaceDocument(doc.id, e.target.files[0])}
                                    />
                                  </label>
                                  <button
                                    onClick={() => handleDeleteDocument(doc.id)}
                                    className="bg-red-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                                  >
                                    Supprimer
                                  </button>
                                </>
                              )}
                              {hasModele && (
                                <button
                                  onClick={() => {
                                    // CORRECTION MOBILE: Navigation directe vers le PDF au lieu du blob
                                    const apiUrl = process.env.REACT_APP_API_URL || '/api';
                                    window.open(`${apiUrl}/documents/modele_fiche_sanitaire/`, '_blank');
                                  }}
                                  className="bg-gray-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors inline-flex items-center gap-1.5"
                                >
                                  <span>📄</span>
                                  <span>Voir le modèle</span>
                                </button>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="mt-2">
                            <p className="text-sm text-gray-500 mb-3">Ce document n'a pas encore été téléchargé</p>
                            {canManageDocuments ? (
                              <div className="space-y-3">
                                <label className="bg-primary-600 text-white px-6 py-3 rounded-lg cursor-pointer hover:bg-primary-700 transition-colors inline-flex items-center gap-2">
                                  <span>📤</span>
                                  <span>Télécharger ce document</span>
                                  <input
                                    type="file"
                                    className="hidden"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={(e) => handleUploadDocument(e, type)}
                                  />
                                </label>

                                {type === 'ATTESTATION_AISANCE_AQUATIQUE' && (
                                  <div className="mt-3">
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-2">
                                      <p className="text-xs text-blue-700 mb-2">
                                        <strong>Votre enfant n'a pas obtenu le Pass'nautique ?</strong>
                                      </p>
                                      <p className="text-xs text-blue-600 mb-2">
                                        Pas de problème ! Vous pouvez déclarer que votre enfant ne l'a pas obtenu.
                                        En revanche il ne pourra pas participer aux activités nautiques, n'hésitez pas à nous contacter si besoin.
                                      </p>
                                    </div>
                                    <button
                                      onClick={() => handleDeclareNonObtenu(type)}
                                      className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2 text-sm"
                                    >
                                      <span>⊘</span>
                                      <span>Déclarer comme non obtenu</span>
                                    </button>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <button
                                disabled
                                className="bg-gray-400 text-white px-6 py-3 rounded-lg cursor-not-allowed inline-flex items-center gap-2 opacity-60"
                              >
                                <span>📤</span>
                                <span>Téléchargement désactivé</span>
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Section Documents Autres */}
            <div className="mt-6 sm:mt-8 border-t pt-4 sm:pt-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                <div className="flex-1">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900">Documents autres (optionnels)</h3>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">
                    Vous pouvez ajouter jusqu'à 4 documents supplémentaires si nécessaire
                  </p>
                </div>
                {canManageDocuments && documents.filter(d => d.type_document === 'AUTRE').length < 4 && (
                  <button
                    onClick={handleOpenDocumentAutreForm}
                    className="w-full sm:w-auto bg-green-600 text-white px-4 sm:px-5 py-2.5 rounded-lg hover:bg-green-700 active:bg-green-800 transition-colors inline-flex items-center justify-center gap-2 font-medium text-sm flex-shrink-0"
                  >
                    <span className="text-lg">➕</span>
                    <span>Ajouter un document</span>
                  </button>
                )}
              </div>

              {/* Liste des documents autres */}
              <div className="space-y-4">
                {documents.filter(d => d.type_document === 'AUTRE').length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-gray-500 text-sm">Aucun document supplémentaire ajouté</p>
                    {canManageDocuments && (
                      <button
                        onClick={handleOpenDocumentAutreForm}
                        className="mt-3 text-primary-600 hover:text-primary-700 text-sm font-medium"
                      >
                        Cliquez ici pour ajouter votre premier document
                      </button>
                    )}
                  </div>
                ) : (
                  documents
                    .filter(d => d.type_document === 'AUTRE')
                    .sort((a, b) => (a.numero_document_autre || 0) - (b.numero_document_autre || 0))
                    .map((doc) => (
                      <div key={doc.id} className="border border-gray-200 rounded-lg p-5 bg-gray-50 hover:bg-gray-100 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-2xl">📎</span>
                              <h4 className="font-semibold text-gray-900">{doc.titre}</h4>
                              <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded font-medium">
                                Document {doc.numero_document_autre}
                              </span>
                            </div>

                            {doc.commentaire_parent && (
                              <div className="mb-3 bg-blue-50 border border-blue-200 rounded p-2">
                                <p className="text-xs text-blue-900">
                                  <span className="font-medium">💬 Votre commentaire :</span> {doc.commentaire_parent}
                                </p>
                              </div>
                            )}

                            <div className="flex items-center gap-2 mb-3">
                              {doc.statut_validation === 'VALIDE' && (
                                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                                  <span className="text-green-600 text-xl">✓</span>
                                  <div>
                                    <p className="text-sm font-medium text-green-800">Document validé</p>
                                    <p className="text-xs text-green-600">Votre document a été approuvé par notre équipe</p>
                                  </div>
                                </div>
                              )}
                              {doc.statut_validation === 'EN_ATTENTE' && (
                                <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                                  <span className="text-blue-600 text-xl">⏳</span>
                                  <div>
                                    <p className="text-sm font-medium text-blue-800">En cours de vérification</p>
                                    <p className="text-xs text-blue-600">Notre équipe vérifie votre document</p>
                                  </div>
                                </div>
                              )}
                              {doc.statut_validation === 'REFUSE' && (
                                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                                  <span className="text-red-600 text-xl">✗</span>
                                  <div>
                                    <p className="text-sm font-medium text-red-800">Document refusé</p>
                                    <p className="text-xs text-red-600">Veuillez télécharger un nouveau document</p>
                                    {doc.commentaire_validation && (
                                      <p className="text-xs text-red-600 mt-1">Raison : {doc.commentaire_validation}</p>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="flex gap-2 flex-wrap">
                              {doc.statut_validation !== 'VALIDE' && canManageDocuments && (
                                <>
                                  <label className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg cursor-pointer hover:bg-blue-700 transition-colors">
                                    Remplacer le document
                                    <input
                                      type="file"
                                      className="hidden"
                                      accept=".pdf,.jpg,.jpeg,.png"
                                      onChange={(e) => handleReplaceDocument(doc.id, e.target.files[0])}
                                    />
                                  </label>
                                  <button
                                    onClick={() => handleDeleteDocument(doc.id)}
                                    className="bg-red-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                                  >
                                    Supprimer
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                )}
              </div>

              {/* Afficher le message si limite atteinte */}
              {documents.filter(d => d.type_document === 'AUTRE').length >= 4 && (
                <div className="mt-4 bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <p className="text-sm text-orange-800 font-medium">
                    ⚠️ Vous avez atteint la limite de 4 documents supplémentaires
                  </p>
                </div>
              )}
            </div>

            <div className="mt-6 bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <strong>À savoir :</strong> Une fois téléchargés, vos documents seront examinés par notre équipe dans un délai de 48h.
                    Vous recevrez une notification par email dès qu'ils seront validés ou si des modifications sont nécessaires.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Onglet Paiements et Aides */}
        {activeTab === 'paiements' && (
          <div className="bg-white shadow rounded-lg p-6">
            {/* Récapitulatif financier */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Récapitulatif financier</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">Prix du séjour</p>
                  <p className="text-2xl font-bold text-gray-900">{(parseFloat(inscription.montant_total) || 0).toFixed(2)} €</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">Aides obtenues</p>
                  <p className="text-2xl font-bold text-green-600">- {(parseFloat(inscription.total_aides) || 0).toFixed(2)} €</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">Prix final</p>
                  <p className="text-2xl font-bold text-blue-600">{(parseFloat(inscription.prix_final) || 0).toFixed(2)} €</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">Reste à payer</p>
                  <p className={`text-2xl font-bold ${soldeRestant > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                    {soldeRestant.toFixed(2)} €
                  </p>
                </div>
              </div>
            </div>

            {/* Section Aides financières */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Aides financières</h3>
              <p className="text-sm text-gray-600 mb-4">
                Les aides financières sont ajoutées et gérées par notre équipe administrative.
              </p>

              {/* Message si aucune aide CAF possible */}
              {inscription.statut_verification_caf === 'AUCUNE_AIDE' && (
                <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded mb-4">
                  <div className="flex items-start">
                    <svg className="h-5 w-5 text-orange-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <h4 className="font-medium text-orange-900 mb-1">Aide CAF</h4>
                      <p className="text-sm text-orange-800">
                        Après vérification de votre numéro d'allocataire, votre enfant n'est pas éligible aux aides de la CAF pour cette inscription.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {aides.length > 0 ? (
                <div className="space-y-4">
                  {aides.map((aide) => (
                    <div key={aide.id} className="border border-gray-200 rounded-lg p-5 bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <span className="text-2xl">
                              {aide.type_aide === 'CAF' && '🏛️'}
                              {aide.type_aide === 'COMMUNE' && '🏛️'}
                              {aide.type_aide === 'ASSOCIATION' && '🤝'}
                              {aide.type_aide === 'BOURSE' && '🎓'}
                              {aide.type_aide === 'AUTRE' && '💰'}
                            </span>
                            <div>
                              <h4 className="font-semibold text-gray-900">{aide.organisme}</h4>
                              <p className="text-sm text-gray-500">{aide.type_aide}</p>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4 mb-3">
                            <div>
                              <p className="text-xs text-gray-500">Montant</p>
                              <p className="text-xl font-bold text-green-600">{aide.montant} €</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Statut</p>
                              {aide.statut_accord === 'ACCORDE' && (
                                <div className="flex items-center gap-2 text-green-700">
                                  <span className="text-lg">✓</span>
                                  <span className="font-medium">Validé par l'organisme</span>
                                </div>
                              )}
                              {aide.statut_accord === 'EN_ATTENTE' && (
                                <div className="flex items-center gap-2 text-blue-700">
                                  <span className="text-lg">⏳</span>
                                  <span className="font-medium">En attente de validation</span>
                                </div>
                              )}
                              {aide.statut_accord === 'REFUSE' && (
                                <div className="flex items-center gap-2 text-red-700">
                                  <span className="text-lg">✗</span>
                                  <span className="font-medium">Refusé</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {aide.commentaire && (
                            <div className="mt-2 text-sm text-gray-600 bg-white p-3 rounded border border-gray-200">
                              <p className="font-medium text-gray-700 mb-1">Commentaire :</p>
                              <p>{aide.commentaire}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-gray-600">Aucune aide financière enregistrée pour le moment</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Pensez à renseigner votre numéro d'allocataire CAF dans vos informations, nous vérifierons pour vous votre éligibilité aux aides. Pour les autres types d'aides, contactez-nous par mail à cdccolonies@gmail.com
                  </p>
                </div>
              )}
            </div>

            {/* Section Paiements */}
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Échéancier de paiement</h3>

              {/* Informations sur les modalités de paiement */}
              <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setShowPaymentInfo(!showPaymentInfo)}
                  className="w-full p-4 flex items-center justify-between hover:bg-blue-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <InformationCircleIcon className="h-5 w-5 text-blue-600" />
                    <h4 className="font-semibold text-gray-900">Comment effectuer vos paiements ?</h4>
                  </div>
                  <span className="text-gray-500">{showPaymentInfo ? '−' : '+'}</span>
                </button>

                {showPaymentInfo && (
                  <div className="p-4 pt-0 space-y-3 border-t border-blue-200">
                    <div className="flex gap-3 p-3 bg-white rounded-lg">
                      <span className="text-2xl">💳</span>
                      <div>
                        <p className="font-semibold text-gray-900">Carte bancaire</p>
                        <p className="text-sm text-gray-600">
                          Paiement sécurisé en ligne via Hello Asso. Validation automatique et immédiate.
                        </p>
                        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
                          <p className="text-xs text-green-800">
                            ✅ <strong>Nouveau :</strong> Votre paiement est validé instantanément.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3 p-3 bg-white rounded-lg">
                      <span className="text-2xl">📝</span>
                      <div>
                        <p className="font-semibold text-gray-900">Chèque</p>
                        <p className="text-sm text-gray-600">
                          À l'ordre de <strong>Association Crions d'Couleurs</strong>. Envoyez vos chèques à l'adresse : <strong>Crions d'Couleurs - 27 Rue Notre-Dame, 35500 Vitré</strong>.<br />
                          Merci d'indiquer au dos du chèque : la date d'encaissement souhaitée et le prénom + nom de l'enfant.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-3 p-3 bg-white rounded-lg">
                      <span className="text-2xl">🏦</span>
                      <div>
                        <p className="font-semibold text-gray-900">Virement bancaire</p>
                        <p className="text-sm text-gray-600">
                          Contactez-nous par mail à <a href="mailto:cdccolonies@gmail.com" className="text-blue-600 hover:underline">cdccolonies@gmail.com</a> pour recevoir nos coordonnées bancaires (IBAN et BIC).<br />
                          Merci d'indiquer votre numéro d'inscription en référence.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-3 p-3 bg-white rounded-lg">
                      <span className="text-2xl">🏖️</span>
                      <div>
                        <p className="font-semibold text-gray-900">Chèques vacances</p>
                        <p className="text-sm text-gray-600">
                          Nous acceptons les chèques vacances ANCV. À envoyer par courrier à l'adresse : <strong>Crions d'Couleurs - 27 Rue Notre-Dame, 35500 Vitré</strong>.<br />
                          Merci de joindre un papier avec le nom, prénom et le séjour d'inscription de l'enfant.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-3 p-3 bg-white rounded-lg">
                      <span className="text-2xl">💵</span>
                      <div>
                        <p className="font-semibold text-gray-900">Espèces</p>
                        <p className="text-sm text-gray-600">
                          Pour un paiement en espèces, merci de nous contacter par mail à <a href="mailto:cdccolonies@gmail.com" className="text-blue-600 hover:underline">cdccolonies@gmail.com</a>.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Message d'avertissement si inscription annulée ou en attente validation partenaire */}
              {!canManagePaiements && (
                <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
                  <div className="flex">
                    <div className="ml-3">
                      <p className="text-sm text-red-700 font-medium">
                        {inscription.statut === 'ANNULEE'
                          ? '⛔ Cette inscription est annulée. Vous ne pouvez pas créer ou modifier l\'échéancier de paiement.'
                          : '⏳ Cette inscription est en attente de validation par nos partenaires sociaux. Vous pourrez créer l\'échéancier une fois l\'inscription validée.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Message de validation du total */}
              {paiements.length > 0 && Math.abs(totalDiff) > 0.01 && (
                <div className={`mb-4 p-4 rounded-lg border-l-4 ${totalDiff > 0 ? 'bg-orange-50 border-orange-400' : 'bg-red-50 border-red-400'}`}>
                  <p className={`text-sm font-medium ${totalDiff > 0 ? 'text-orange-800' : 'text-red-800'}`}>
                    {totalDiff > 0
                      ? `⚠️ Il manque ${totalDiff.toFixed(2)} € dans votre échéancier (Total échéancier: ${paiementsTotal.toFixed(2)} € / Prix final: ${prixFinal.toFixed(2)} €)`
                      : `⚠️ Votre échéancier dépasse de ${Math.abs(totalDiff).toFixed(2)} € (Total échéancier: ${paiementsTotal.toFixed(2)} € / Prix final: ${prixFinal.toFixed(2)} €)`
                    }
                  </p>
                </div>
              )}

              {soldeRestant > 0 && !paiements.some(p => p.statut !== 'VALIDE') && !showCustomForm && canManagePaiements && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                  <h4 className="font-semibold text-gray-900 mb-4">Créer votre échéancier de paiement</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Montant restant à payer : <strong>{soldeRestant.toFixed(2)} €</strong>
                  </p>
                  {paiements.some(p => p.statut === 'VALIDE') && (
                    <div className="mb-4 bg-green-50 border border-green-200 rounded p-3 text-sm text-green-800">
                      ℹ️ Vous avez déjà {paiements.filter(p => p.statut === 'VALIDE').length} paiement(s) validé(s). Vous pouvez maintenant créer un échéancier pour le solde restant.
                    </div>
                  )}
                  <div className="flex flex-wrap gap-3 items-center">
                    <label className="text-sm font-medium text-gray-700">Nombre d'échéances :</label>
                    <input
                      type="number"
                      min="1"
                      value={nombreEcheances}
                      onChange={(e) => setNombreEcheances(parseInt(e.target.value) || 1)}
                      onFocus={(e) => e.target.select()}
                      className="border border-gray-300 rounded-lg px-4 py-2 w-24"
                    />
                    <button
                      onClick={() => initCustomEcheances(nombreEcheances)}
                      className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors font-medium"
                    >
                      Créer l'échéancier
                    </button>
                  </div>
                </div>
              )}

              {showCustomForm && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                  <h4 className="font-semibold text-gray-900 mb-4">Personnaliser votre échéancier</h4>

                  <div className="space-y-4 mb-4">
                    {echeancesCustom.map((ech, index) => (
                      <div key={index} className="bg-white rounded-lg p-4 border border-gray-300">
                        <h5 className="font-medium text-gray-900 mb-3">Échéance {index + 1}</h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Montant (€)</label>
                            <input
                              type="number"
                              step="0.01"
                              value={ech.montant}
                              onChange={(e) => handleEcheanceChange(index, 'montant', e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Mode de paiement</label>
                            <select
                              value={ech.mode_paiement}
                              onChange={(e) => handleEcheanceChange(index, 'mode_paiement', e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2"
                            >
                              <option value="CARTE">Carte bancaire</option>
                              <option value="CHEQUE">Chèque</option>
                              <option value="VIREMENT">Virement</option>
                              <option value="CHEQUE_VACANCES">Chèques vacances</option>
                              <option value="ESPECES">Espèces</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Date (optionnel)</label>
                            <input
                              type="date"
                              value={ech.date_echeance}
                              onChange={(e) => handleEcheanceChange(index, 'date_echeance', e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-gray-300 mb-4">
                    <p className="text-sm">
                      <strong>Total de l'échéancier :</strong> {calculateTotalEcheances().toFixed(2)} €
                      <span className={`ml-2 ${Math.abs(calculateTotalEcheances() - soldeRestant) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
                        {Math.abs(calculateTotalEcheances() - soldeRestant) < 0.01 ? '✓ Correct' : `(Attendu: ${soldeRestant.toFixed(2)} €)`}
                      </span>
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={handleCreerEcheancierCustom}
                      className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium"
                    >
                      Valider l'échéancier
                    </button>
                    <button
                      onClick={() => {
                        setShowCustomForm(false);
                        setEcheancesCustom([]);
                      }}
                      className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors font-medium"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}

              {paiements.length > 0 && canManagePaiements && (
                <div className="mb-4 flex gap-3">
                  {paiements.some(p => p.statut === 'VALIDE') ? (
                    // S'il y a des paiements validés, on ne peut supprimer que les non validés
                    paiements.some(p => p.statut !== 'VALIDE') && (
                      <button
                        onClick={handleSupprimerPaiementsNonValides}
                        className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors text-sm"
                      >
                        Supprimer les paiements non validés et redéfinir l'échéancier
                      </button>
                    )
                  ) : (
                    // S'il n'y a aucun paiement validé, on peut supprimer tout l'échéancier
                    <button
                      onClick={handleSupprimerEcheancier}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm"
                    >
                      Supprimer l'échéancier et en créer un nouveau
                    </button>
                  )}
                </div>
              )}

              {paiements.length > 0 ? (
                <div className="space-y-3">
                  {paiements.map((p) => {
                    const isEditing = editingPaiements[p.id];
                    const canEditPaiement = p.statut !== 'VALIDE';

                    return (
                      <div key={p.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="text-2xl">
                                {(isEditing ? isEditing.mode_paiement : p.mode_paiement) === 'CARTE' && '💳'}
                                {(isEditing ? isEditing.mode_paiement : p.mode_paiement) === 'CHEQUE' && '📝'}
                                {(isEditing ? isEditing.mode_paiement : p.mode_paiement) === 'VIREMENT' && '🏦'}
                                {(isEditing ? isEditing.mode_paiement : p.mode_paiement) === 'CHEQUE_VACANCES' && '🏖️'}
                                {(isEditing ? isEditing.mode_paiement : p.mode_paiement) === 'ESPECES' && '💵'}
                              </span>
                              <div className="flex-1">
                                <p className="font-semibold text-gray-900">Échéance {p.numero_echeance}</p>
                                {!isEditing ? (
                                  <p className="text-sm text-gray-600">
                                    {p.mode_paiement === 'CARTE' && 'Carte bancaire'}
                                    {p.mode_paiement === 'CHEQUE' && 'Chèque'}
                                    {p.mode_paiement === 'VIREMENT' && 'Virement'}
                                    {p.mode_paiement === 'CHEQUE_VACANCES' && 'Chèques vacances'}
                                    {p.mode_paiement === 'ESPECES' && 'Espèces'}
                                  </p>
                                ) : (
                                  <select
                                    value={isEditing.mode_paiement}
                                    onChange={(e) => setEditingPaiements({
                                      ...editingPaiements,
                                      [p.id]: { ...isEditing, mode_paiement: e.target.value }
                                    })}
                                    className="text-sm border border-gray-300 rounded px-2 py-1 mt-1"
                                  >
                                    <option value="CARTE">Carte bancaire</option>
                                    <option value="CHEQUE">Chèque</option>
                                    <option value="VIREMENT">Virement</option>
                                    <option value="CHEQUE_VACANCES">Chèques vacances</option>
                                    <option value="ESPECES">Espèces</option>
                                  </select>
                                )}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-gray-600">Montant : </span>
                                {!isEditing ? (
                                  <span className="font-bold text-gray-900">{p.montant} €</span>
                                ) : (
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={isEditing.montant}
                                    onChange={(e) => setEditingPaiements({
                                      ...editingPaiements,
                                      [p.id]: { ...isEditing, montant: e.target.value }
                                    })}
                                    className="border border-gray-300 rounded px-2 py-1 w-32"
                                  />
                                )}
                              </div>
                              {p.date_echeance && (
                                <div>
                                  <span className="text-gray-600">Date : </span>
                                  <span className="font-medium text-gray-900">
                                    {new Date(p.date_echeance).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                  </span>
                                </div>
                              )}
                            </div>
                            {p.reference && (
                              <p className="text-xs text-gray-500 mt-2">Référence : {p.reference}</p>
                            )}

                            {/* Avertissement délai de validation pour les paiements par carte - SUPPRIMÉ car maintenant automatique */}

                            {canEditPaiement && !isEditing && canManagePaiements && (
                              <button
                                onClick={() => handleEditPaiement(p.id)}
                                className="mt-3 text-sm text-blue-600 hover:text-blue-800"
                              >
                                ✏️ Modifier
                              </button>
                            )}

                            {isEditing && (
                              <div className="mt-3 flex gap-2">
                                <button
                                  onClick={() => handleSavePaiement(p.id)}
                                  className="text-sm bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                                >
                                  Enregistrer
                                </button>
                                <button
                                  onClick={() => handleCancelEditPaiement(p.id)}
                                  className="text-sm bg-gray-600 text-white px-3 py-1 rounded hover:bg-gray-700"
                                >
                                  Annuler
                                </button>
                              </div>
                            )}
                          </div>

                          <div className="ml-4">
                            <div className="space-y-2">
                              {p.statut === 'VALIDE' && (
                                <div className="bg-green-100 border border-green-300 rounded-lg px-4 py-2 text-center">
                                  <span className="text-green-700 text-xl block">✓</span>
                                  <span className="text-xs font-medium text-green-700">Payé</span>
                                </div>
                              )}
                              {p.statut === 'EN_ATTENTE' && (
                                <div className="bg-blue-100 border border-blue-300 rounded-lg px-4 py-2 text-center">
                                  <span className="text-blue-700 text-xl block">⏳</span>
                                  <span className="text-xs font-medium text-blue-700">En attente</span>
                                </div>
                              )}
                              {p.statut === 'REFUSE' && (
                                <div className="bg-red-100 border border-red-300 rounded-lg px-4 py-2 text-center">
                                  <span className="text-red-700 text-xl block">✗</span>
                                  <span className="text-xs font-medium text-red-700">Refusé</span>
                                </div>
                              )}

                              {/* Bouton Payer pour les paiements par carte non validés */}
                              {p.mode_paiement === 'CARTE' && p.statut !== 'VALIDE' && canManagePaiements && !isEditing && (
                                <button
                                  onClick={async () => {
                                    try {
                                      const response = await paiementService.initierPaiementHelloAsso(p.id);
                                      if (response.data.redirect_url) {
                                        window.location.href = response.data.redirect_url;
                                      } else {
                                        notify.error("Erreur: Impossible de récupérer l'URL de paiement");
                                      }
                                    } catch (error) {
                                      console.error(error);
                                      notify.error("Erreur lors de l'initialisation du paiement Hello Asso");
                                    }
                                  }}
                                  className="w-full bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                                >
                                  💳 Payer
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                soldeRestant === 0 && (
                  <div className="text-center py-12 bg-green-50 rounded-lg border border-green-200">
                    <span className="text-6xl mb-4 block">✅</span>
                    <p className="text-green-700 font-semibold text-lg">Paiement complet !</p>
                    <p className="text-sm text-green-600 mt-2">
                      Votre inscription est entièrement payée
                    </p>
                  </div>
                )
              )}

              <div className="mt-6 bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      <strong>Modes de paiement :</strong> Carte bancaire, Chèque, Virement, Chèques vacances ou Espèces.
                      Vous pouvez payer en plusieurs fois sans frais supplémentaires. Les aides sont déduites automatiquement du prix final.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Formulaire Document Autre */}
        {showDocumentAutreForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Ajouter un document supplémentaire</h3>
                <button
                  onClick={handleCloseDocumentAutreForm}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmitDocumentAutre} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type de document <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={documentAutreData.titre}
                    onChange={(e) => setDocumentAutreData({ ...documentAutreData, titre: e.target.value })}
                    placeholder="Ex: Certificat médical, Autorisation parentale, etc."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Indiquez quel type de document vous envoyez</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Commentaire (optionnel)
                  </label>
                  <textarea
                    value={documentAutreData.commentaire}
                    onChange={(e) => setDocumentAutreData({ ...documentAutreData, commentaire: e.target.value })}
                    placeholder="Ex: Pour allergie alimentaire, Pour activité spécifique, etc."
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Ajoutez des précisions si nécessaire</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fichier <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setDocumentAutreData({ ...documentAutreData, fichier: e.target.files[0] })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Formats acceptés : PDF, JPG, PNG (10 MB max)</p>
                  {documentAutreData.fichier && (
                    <p className="text-xs text-green-600 mt-2">
                      ✓ Fichier sélectionné : {documentAutreData.fichier.name}
                    </p>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseDocumentAutreForm}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                  >
                    Ajouter le document
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}


      </div>
    </div>
  );
};

export default InscriptionDetail;
